import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';
import { SectionLabel } from '../components/ui/SectionLabel';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import {
  AdminError,
  createKey,
  createPairing,
  deleteKey,
  deletePairing,
  listKeys,
  listPairings,
  login,
  type AccessKeyView,
  type IssuedAccessKey,
  type PairingInvite,
  type PendingPairing,
} from '../admin/adminApi';

/**
 * Panoul de administrare, la `/admin`.
 *
 * Stă **în afara** `SessionProvider`: nu cere bilet și nu deschide socketul.
 * Asta e intenționat — pagina asta e calea de recuperare. Când cheia
 * dashboardului e greșită sau revocată, biletul e refuzat și restul aplicației
 * arată ecranul de mentenanță; exact atunci ai nevoie să intri aici și să
 * generezi alta. Dacă ar depinde de bilet, s-ar închide împreună cu el.
 *
 * Tokenul de sesiune stă doar în memorie, ca și biletul: un refresh cere parola
 * din nou. E o pagină pe care o deschizi de câteva ori pe an, deci comoditatea
 * nu merită un token lăsat în localStorage.
 */
export function AdminPage() {
  const [token, setToken] = useState<string | null>(null);

  return (
    <Screen>
      {token === null ? (
        <LoginCard onAuthenticated={setToken} />
      ) : (
        <KeysPanel token={token} onExpired={() => setToken(null)} />
      )}
    </Screen>
  );
}

/** Fundalul comun, ca pagina să semene cu restul aplicației. */
function Screen({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.weather.bg,
        color: theme.palette.weather.ink,
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: 'clamp(20px, 4vw, 48px)',
        transition: 'background-color .5s ease, color .5s ease',
      })}
    >
      {children}
    </Box>
  );
}

function errorText(error: unknown): string {
  if (error instanceof AdminError) return error.message;
  return error instanceof Error ? error.message : 'Ceva n-a mers.';
}

function formatMoment(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ro-RO');
}

/** Doar ora, pentru ceva care expiră în minute: „17:04". */
function formatClock(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ?
      '—'
    : date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

/** Parola din `ADMIN_PASSWORD`. Nu există utilizatori, deci nici câmp de nume. */
function LoginCard({ onAuthenticated }: { onAuthenticated: (token: string) => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || password === '') return;

    setBusy(true);
    setError(null);
    login(password)
      .then((session) => onAuthenticated(session.token))
      .catch((err: unknown) => {
        setError(errorText(err));
        setPassword('');
      })
      .finally(() => setBusy(false));
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{ maxWidth: 380, width: '100%', marginTop: 'clamp(40px, 12vh, 120px)' }}
    >
      <SurfaceCard sx={{ gap: '16px' }}>
        <SectionLabel>Administrare</SectionLabel>

        <Typography component="h1" sx={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
          Chei de acces
        </Typography>

        <TextField
          type="password"
          label="Parolă"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoFocus
          fullWidth
          size="small"
          disabled={busy}
        />

        {error !== null && <Message tone="bad">{error}</Message>}

        <Button
          type="submit"
          disabled={busy || password === ''}
          sx={(theme) => ({
            height: 40,
            borderRadius: `${theme.radii.pill}px`,
            backgroundColor: theme.palette.weather.accentSoft,
            color: theme.palette.weather.accent,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { backgroundColor: theme.palette.weather.accentSoft, boxShadow: 'none' },
          })}
        >
          {busy ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Intră'}
        </Button>
      </SurfaceCard>
    </Box>
  );
}

interface KeysPanelProps {
  token: string;
  onExpired: () => void;
}

function KeysPanel({ token, onExpired }: KeysPanelProps) {
  const [keys, setKeys] = useState<AccessKeyView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  /** Cheia proaspăt generată. Se vede o singură dată — backendul ține doar amprenta. */
  const [issued, setIssued] = useState<IssuedAccessKey | null>(null);

  /** O sesiune expirată nu e o eroare de afișat, ci un motiv să ceri parola. */
  const handle = useCallback(
    (err: unknown) => {
      if (err instanceof AdminError && err.status === 401) {
        onExpired();
        return;
      }
      setError(errorText(err));
    },
    [onExpired],
  );

  const reload = useCallback(() => {
    listKeys(token)
      .then((rows) => {
        setKeys(rows);
        setError(null);
      })
      .catch(handle);
  }, [token, handle]);

  useEffect(reload, [reload]);

  const generate = (event: FormEvent) => {
    event.preventDefault();
    if (busy || name.trim() === '') return;

    setBusy(true);
    setError(null);
    createKey(token, name.trim())
      .then((key) => {
        setIssued(key);
        setName('');
        reload();
      })
      .catch(handle)
      .finally(() => setBusy(false));
  };

  /**
   * Ștergerea e definitivă și fără urmă în listă, deci se întreabă întâi.
   * Dispozitivul care folosea cheia pică la următorul bilet, în cel mult un
   * sfert de oră.
   */
  const remove = (key: AccessKeyView) => {
    const sure = window.confirm(
      `Ștergi cheia „${key.name}”? Dispozitivul care o folosește pierde accesul.`,
    );
    if (!sure) return;

    deleteKey(token, key.id).then(reload).catch(handle);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 720, display: 'grid', gap: '18px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SectionLabel>Chei de acces</SectionLabel>
        <Button
          onClick={onExpired}
          startIcon={<LogoutRoundedIcon />}
          sx={(theme) => ({
            color: theme.palette.weather.soft,
            fontSize: 13,
            textTransform: 'none',
          })}
        >
          Ieși
        </Button>
      </Box>

      {issued !== null && <IssuedKeyCard issued={issued} onDismiss={() => setIssued(null)} />}

      <PairingCard token={token} onError={handle} onPaired={reload} />

      <Box component="form" onSubmit={generate}>
        <SurfaceCard sx={{ gap: '12px' }}>
          <Typography sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Cheie în clar (pentru scripturi)
          </Typography>
          <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
            Pentru un telefon folosește codul de mai sus. Asta e pentru `curl` sau alt serviciu,
            unde ai nevoie de cheie ca text.
          </Typography>

          <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <TextField
              label="Nume"
              placeholder="Telefon Dani"
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              disabled={busy}
              sx={{ flex: '1 1 220px' }}
            />
            <Button
              type="submit"
              disabled={busy || name.trim() === ''}
              startIcon={busy ? undefined : <AddRoundedIcon />}
              sx={(theme) => ({
                height: 40,
                padding: '0 18px',
                borderRadius: `${theme.radii.pill}px`,
                backgroundColor: theme.palette.weather.accentSoft,
                color: theme.palette.weather.accent,
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { backgroundColor: theme.palette.weather.accentSoft, boxShadow: 'none' },
              })}
            >
              {busy ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Generează'}
            </Button>
          </Box>
        </SurfaceCard>
      </Box>

      {error !== null && <Message tone="bad">{error}</Message>}

      {keys === null ? (
        <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
          Se încarcă…
        </Typography>
      ) : keys.length === 0 ? (
        <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
          Nicio cheie încă.
        </Typography>
      ) : (
        keys.map((key) => <KeyRow key={key.id} entry={key} onDelete={() => remove(key)} />)
      )}
    </Box>
  );
}

/**
 * Conectarea unui telefon, în doi pași și fără nimic de tastat greșit.
 *
 * Cardul ăsta nu arată nicio cheie, fiindcă la momentul generării nu există
 * niciuna: codul e o invitație, iar cheia se naște abia când telefonul o
 * folosește. Șase cifre, fiindcă se bat pe tastatura numerică și nu poate fi
 * confundat `O` cu `0`.
 */
function PairingCard({
  token,
  onError,
  onPaired,
}: {
  token: string;
  onError: (err: unknown) => void;
  /** Chemată când un telefon tocmai și-a luat cheia, ca lista să se reîncarce. */
  onPaired: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState<PairingInvite | null>(null);
  const [left, setLeft] = useState(0);
  /** Codurile emise și încă nefolosite — inclusiv cel de deasupra. */
  const [pending, setPending] = useState<PendingPairing[]>([]);
  /** Numele telefonului care tocmai s-a conectat, cât să se vadă confirmarea. */
  const [claimed, setClaimed] = useState<string | null>(null);

  /**
   * Lista dinainte, ca să se vadă ce a dispărut între două verificări.
   *
   * O ținem în ref, nu în state: e o comparație, nu ceva de desenat, iar dintr-un
   * `setPending(previous => …)` n-am avea voie să pornim alte actualizări.
   */
  const seen = useRef<PendingPairing[]>([]);

  const reloadPending = useCallback(() => {
    listPairings(token)
      .then((rows) => {
        // Un cod care era în listă, nu mai e, și încă nu-i expirase timpul, a
        // fost folosit: telefonul și-a luat cheia chiar acum.
        const redeemed = seen.current.filter(
          (entry) =>
            !rows.some((row) => row.id === entry.id) && Date.parse(entry.expiresAt) > Date.now(),
        );

        seen.current = rows;
        setPending(rows);

        if (redeemed.length > 0) {
          setClaimed(redeemed[0].name);
          setInvite(null);
          onPaired();
        }
      })
      .catch(onError);
  }, [token, onError, onPaired]);

  useEffect(reloadPending, [reloadPending]);

  /**
   * Cât timp un cod așteaptă, întrebăm din trei în trei secunde.
   *
   * Altfel n-ai cum afla că telefonul s-a conectat decât dând refresh — iar
   * refreshul cere parola din nou, fiindcă sesiunea stă doar în memorie.
   * Interogarea se oprește singură când nu mai e nimic în așteptare.
   */
  useEffect(() => {
    if (pending.length === 0) return;

    const timer = window.setInterval(reloadPending, 3000);
    return () => window.clearInterval(timer);
  }, [pending.length, reloadPending]);

  // Numărătoarea inversă: un cod expirat trebuie să arate expirat, nu valid.
  useEffect(() => {
    if (invite === null) return;
    setLeft(invite.expiresIn);
    const timer = window.setInterval(() => {
      setLeft((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [invite]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || name.trim() === '') return;

    setBusy(true);
    setClaimed(null);
    createPairing(token, name.trim())
      .then((next) => {
        setInvite(next);
        setName('');
        reloadPending();
      })
      .catch(onError)
      .finally(() => setBusy(false));
  };

  /** Renunțarea la un cod nefolosit; dacă era cel afișat, ascundem și cifrele. */
  const cancel = (entry: PendingPairing) => {
    deletePairing(token, entry.id)
      .then(() => {
        if (invite !== null && invite.name === entry.name) setInvite(null);
        // Scos din lista de referință înainte de reîncărcare: altfel dispariția
        // lui ar arăta exact ca o împerechere reușită.
        seen.current = seen.current.filter((row) => row.id !== entry.id);
        reloadPending();
      })
      .catch(onError);
  };

  const minutes = Math.floor(left / 60);
  const seconds = String(left % 60).padStart(2, '0');

  return (
    <Box component="form" onSubmit={submit}>
      <SurfaceCard sx={{ gap: '12px' }}>
        <Typography sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          Conectează un telefon
        </Typography>
        <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
          Dă-i un nume, apoi bate codul în aplicație. Cheia se creează pe telefon — aici nu apare
          și nu trebuie copiată nicăieri.
        </Typography>

        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <TextField
            label="Nume dispozitiv"
            placeholder="Telefon Dani"
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            disabled={busy}
            sx={{ flex: '1 1 220px' }}
          />
          <Button
            type="submit"
            disabled={busy || name.trim() === ''}
            startIcon={busy ? undefined : <SmartphoneRoundedIcon />}
            sx={(theme) => ({
              height: 40,
              padding: '0 18px',
              borderRadius: `${theme.radii.pill}px`,
              backgroundColor: theme.palette.weather.accent,
              color: '#fff',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { backgroundColor: theme.palette.weather.accent, boxShadow: 'none' },
            })}
          >
            {busy ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Generează cod'}
          </Button>
        </Box>

        {invite !== null && (
          <Box
            sx={(theme) => ({
              marginTop: '4px',
              padding: '18px 20px',
              borderRadius: `${theme.radii.card / 2}px`,
              backgroundColor: theme.palette.weather.bg,
              display: 'grid',
              gap: '6px',
              justifyItems: 'center',
            })}
          >
            <SectionLabel>{invite.name}</SectionLabel>
            <Typography
              sx={(theme) => ({
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 'clamp(34px, 8vw, 46px)',
                fontWeight: 700,
                letterSpacing: '.16em',
                lineHeight: 1.1,
                // Cifrele se citesc cu voce tare de la un ecran la altul; opacul
                // de la expirare spune fără cuvinte că nu mai are rost.
                color: left === 0 ? theme.palette.weather.soft : theme.palette.weather.ink,
                textDecoration: left === 0 ? 'line-through' : 'none',
              })}
            >
              {invite.code}
            </Typography>
            <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.weather.soft })}>
              {left === 0 ?
                'A expirat — generează altul.'
              : `Valabil încă ${minutes}:${seconds}, o singură dată.`}
            </Typography>
          </Box>
        )}

        {claimed !== null && <Message tone="good">„{claimed}” s-a conectat.</Message>}

        {/*
          Ce a mai rămas neridicat. Codurile expirate nu ajung aici: backendul
          le șterge la fiecare listare, ca tabelul să nu adune încercări moarte.
        */}
        {pending.length > 0 && (
          <Box sx={{ display: 'grid', gap: '4px', marginTop: '2px' }}>
            <SectionLabel>În așteptare</SectionLabel>
            {pending.map((entry) => (
              <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                  sx={(theme) => ({
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    color: theme.palette.weather.soft,
                  })}
                >
                  {entry.name} · până la {formatClock(entry.expiresAt)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => cancel(entry)}
                  aria-label={`Anulează codul pentru ${entry.name}`}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </SurfaceCard>
    </Box>
  );
}

/**
 * Singurul loc în care cheia e vizibilă.
 *
 * Backendul păstrează doar amprenta SHA-256, deci nu există „arată-mi-o din
 * nou”: dacă se pierde, se revocă și se generează alta. De aceea cardul ăsta e
 * insistent și nu dispare de la sine.
 */
function IssuedKeyCard({ issued, onDismiss }: { issued: IssuedAccessKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState<boolean | null>(null);

  const copy = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(issued.key);
        setCopied(true);
      } catch {
        // Clipboard-ul cere context securizat (https sau localhost). Pe http
        // simplu nu merge — textul rămâne selectabil manual, deci nu e blocant.
        setCopied(false);
      }
    })();
  };

  return (
    <SurfaceCard
      sx={(theme) => ({ gap: '12px', border: `1px solid ${theme.palette.weather.accent}` })}
    >
      <Typography sx={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        Cheia pentru „{issued.name}”
      </Typography>
      <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
        Copiaz-o acum. Se vede o singură dată — pe server rămâne doar amprenta ei.
      </Typography>

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Box
          component="code"
          sx={(theme) => ({
            flex: 1,
            padding: '10px 12px',
            borderRadius: `${theme.radii.card / 2}px`,
            backgroundColor: theme.palette.weather.bg,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            wordBreak: 'break-all',
            userSelect: 'all',
          })}
        >
          {issued.key}
        </Box>
        <IconButton onClick={copy} aria-label="Copiază cheia">
          <ContentCopyRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {copied === true && <Message tone="good">Copiată.</Message>}
      {copied === false && <Message tone="bad">Nu am putut copia — selecteaz-o manual.</Message>}

      <Button onClick={onDismiss} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
        Am salvat-o
      </Button>
    </SurfaceCard>
  );
}

/**
 * Un dispozitiv cu acces.
 *
 * Toate rândurile de aici sunt vii: ștergerea scoate rândul din tabel, deci nu
 * există stare „revocată” de desenat.
 */
function KeyRow({ entry, onDelete }: { entry: AccessKeyView; onDelete: () => void }) {
  return (
    <SurfaceCard
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: '14px',
        padding: '16px 18px',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: '3px' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{entry.name}</Typography>
        <Typography
          sx={(theme) => ({
            fontSize: 12.5,
            color: theme.palette.weather.soft,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          })}
        >
          {entry.prefix}…
        </Typography>
        <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.weather.soft })}>
          creată {formatMoment(entry.createdAt)} · folosită {formatMoment(entry.lastUsedAt)}
        </Typography>
      </Box>

      <IconButton onClick={onDelete} aria-label={`Șterge cheia ${entry.name}`}>
        <DeleteOutlineRoundedIcon fontSize="small" />
      </IconButton>
    </SurfaceCard>
  );
}

function Message({ tone, children }: { tone: 'good' | 'bad'; children: ReactNode }) {
  return (
    <Typography
      sx={(theme) => ({
        fontSize: 13,
        color: tone === 'bad' ? theme.palette.error.main : theme.palette.weather.accent,
      })}
    >
      {children}
    </Typography>
  );
}
